/* istanbul ignore file*/
// tagging only utils shouldn't require int/unit testing
import allytm from '@ally/allytm'
import dayjs from 'dayjs'
import { PlaidLinkError } from 'react-plaid-link'
import { FormValues } from '../../components/Savings/SavingsToolkit/Boosters/RecurringTransfers/Forms/RecurringTransfersEditFormInner'
import { RecurringTransfersAddFormValues, StkBooster } from '../../types'

interface AccountForRequest {
  boosterAccountCategory: 'F' | 'M' | undefined
  stkAccountId: string | undefined
}

interface BucketForRequest {
  bucketId: string
  allocationAmount: number
}
interface StkTagging {
  createRecurringTransferSubmitEvent: (
    values: RecurringTransfersAddFormValues,
    accountForRequest: AccountForRequest[],
    bucketsForRequest: BucketForRequest[],
  ) => void
  editRecurringTransferSubmitEvent: (
    values: FormValues,
    accountForRequest: AccountForRequest[],
    bucketsForRequest: BucketForRequest[],
    selectedBooster: StkBooster,
  ) => void
}

export const serverErrorEvent = (
  messageDisplayed: string,
  errorCode: string | number | undefined = 'null',
  isFormError = false,
): void => {
  allytm.event('customError', messageDisplayed, errorCode, {
    isFormError,
  })
}

export const stkServerErrors = {
  getAnalyzeServerError: (err: Error): void => {
    serverErrorEvent(
      "This information isn't available right now. Try again later.",
      err?.message,
      false,
    )
  },
  updateChartServerError: (err: Error): void => {
    serverErrorEvent(
      "This information isn't available right now. Try again later.",
      err?.message,
      true,
    )
  },
  getBucketsServerError: (err: Error): void => {
    serverErrorEvent(
      "We're sorry, we couldn't add your bucket at this time. Try again later or call us to get this added now.",
      err?.message,
      false,
    )
  },
  createBucketServerError: (err: Error): void => {
    serverErrorEvent(
      "We're sorry, we couldn't add your bucket at this time. Try again later or call us to get this added now.",
      err?.message,
      true,
    )
  },
  editBucketServerError: (err: Error): void => {
    serverErrorEvent(
      "We're sorry, we couldn't add your bucket at this time. Try again later or call us to get this added now.",
      err?.message,
      true,
    )
  },
  deleteBucketServerError: (err: Error): void => {
    serverErrorEvent(
      "We're sorry, we couldn't add your bucket at this time. Try again later or call us to get this added now.",
      err?.message,
      false,
    )
  },
  redistributeServerError: (err: Error): void => {
    serverErrorEvent(
      "We can't complete your request right now. Try again later.",
      err?.message,
      true,
    )
  },
  transferMoneyServerError: (err: Error): void => {
    serverErrorEvent(
      "We can't complete your request right now. Try again later.",
      err?.message,
      true,
    )
  },
  getRulesServerError: (err: Error): void => {
    serverErrorEvent(
      "We're sorry, we couldn't add your bucket at this time. Try again later or call us to get this added now.",
      err?.message,
      false,
    )
  },
  editRulesServerError: (err: Error): void => {
    serverErrorEvent(
      "We can't complete your request right now. Try again later.",
      err?.message,
      true,
    )
  },
  createGoalServerError: (err: Error): void => {
    serverErrorEvent(
      "We're sorry, we couldn't add your bucket at this time. Try again later or call us to get this added now.",
      err?.message,
      true,
    )
  },
  editGoalServerError: (err: Error): void => {
    serverErrorEvent(
      "We're sorry, we couldn't add your bucket at this time. Try again later or call us to get this added now.",
      err?.message,
      true,
    )
  },
  deleteGoalServerError: (err: Error): void => {
    serverErrorEvent(
      "We're sorry, we couldn't add your bucket at this time. Try again later or call us to get this added now.",
      err?.message,
      false,
    )
  },
  getToolkitAccountsServerError: (err: Error): void => {
    serverErrorEvent(
      "We're sorry, we couldn't add your bucket at this time. Try again later or call us to get this added now.",
      err?.message,
      false,
    )
  },
  getBoostersServerError: (err: Error): void => {
    serverErrorEvent(
      "We're sorry, we couldn't add your bucket at this time. Try again later or call us to get this added now.",
      err?.message,
      false,
    )
  },
  createBoosterServerError: (err: Error, boosterType: string): void => {
    const message = `${boosterType}_${err?.message}`
    serverErrorEvent(
      "We're unable to process your request at this time. Please try again later.",
      message,
      true,
    )
  },
  updateBoosterServerError: (err: Error, boosterType?: string): void => {
    const message = `${boosterType}_${err?.message}`

    serverErrorEvent(
      "We're unable to process your request at this time. Please try again later.",
      message,
      true,
    )
  },
  deleteBoosterServerError: (err: Error, boosterType: string): void => {
    const message = `${boosterType}_${err?.message}`

    serverErrorEvent(
      "We're unable to process your request at this time. Please try again later.",
      message,
      false,
    )
  },
  getBoosterTransactionsServerError: (err: Error): void => {
    serverErrorEvent(
      "This information isn't available right now. Try again later.",
      err?.message,
      false,
    )
  },
  getTransferSpeedsServerError: (err: Error): void => {
    serverErrorEvent(
      "We're sorry, we couldn't add your bucket at this time. Try again later or call us to get this added now.",
      err?.message,
      false,
    )
  },
  stkEnrollmentTimeout: (err: Error): void => {
    serverErrorEvent(
      'There was an error trying to load your buckets and boosters. Please try again later.',
      err?.message,
      false,
    )
  },
  plaidGeneralError: (err: PlaidLinkError): void => {
    serverErrorEvent(
      "We're unable to process your request at this time. Please try again later.",
      err?.error_code,
      false,
    )
  },
  eavGeneralError: (err: Error): void => {
    serverErrorEvent(
      "We're unable to process your request at this time. Please try again later.",
      err?.message,
      false,
    )
  },
  eavInUseError: (err: Error): void => {
    serverErrorEvent(
      'This account is already in use with an active booster. Add a new checking account to continue.',
      err?.message,
      false,
    )
  },
}

export const useStkTagging = (): StkTagging => {
  const createRecurringTransferSubmitEvent = (
    values: RecurringTransfersAddFormValues,
    accountForRequest: AccountForRequest[],
    bucketsForRequest: BucketForRequest[],
  ): void => {
    allytm.event('customEvent', {
      name: 'addRecurringTransfer',
      type: 'userAttribute',
      attr: {
        card: {
          accounts: accountForRequest,
          depositAmount: values.amount || 0,
          firstTransferDate: dayjs(values.firstTransferDate)
            .format('YYYY-MM-DD')
            .toString(),
          boosterStatus: 'Active',
          boosterType: 'SIFI',
          buckets: bucketsForRequest,
          frequency: values.frequency || 1,
        },
      },
    })
  }
  const editRecurringTransferSubmitEvent = (
    values: FormValues,
    accountForRequest: AccountForRequest[],
    bucketsForRequest: BucketForRequest[],
    selectedBooster: StkBooster,
  ): void => {
    allytm.event('customEvent', {
      name: 'addRecurringTransfer',
      type: 'userAttribute',
      attr: {
        card: {
          accounts: accountForRequest,
          depositAmount: values.amount,
          nextPaymentDueDate: selectedBooster.nextPaymentDueDate,
          boosterStatus: selectedBooster.boosterStatus,
          boosterId: selectedBooster.accountBoosterId,
          buckets: bucketsForRequest,
        },
      },
    })
  }

  return {
    createRecurringTransferSubmitEvent,
    editRecurringTransferSubmitEvent,
  }
}
